import { useState } from "react";
import { Check, X, Snowflake, Pill, Award } from "lucide-react";
import ScrollReveal from "@/components/ScrollReveal";

const COMPETITORS = [
  {
    id: "xcellereyes",
    name: "XcellerEYES",
    sub: "Lyophilized Amniotic Membrane",
    icon: Award,
    highlight: true,
    rows: [
      { label: "Ambient temperature storage", value: true, detail: "Up to 24 months — no freezer required." },
      { label: "Preserved extracellular matrix", value: true, detail: "Aseptic processing protects native ECM and endogenous proteins." },
      { label: "Single-step application", value: true, detail: "Direct or BCL — no rehydration needed." },
      { label: "Simplified logistics & reimbursement", value: true, detail: "Streamlined for in-office or surgical workflows." },
      { label: "Reduced waste", value: true, detail: "Long shelf life means fewer expirations." },
    ],
  },
  {
    id: "dryeye",
    name: "Traditional Dry Eye Management",
    sub: "Drops, plugs, lifestyle",
    icon: Pill,
    rows: [
      { label: "Ambient temperature storage", value: true, detail: "Generally yes." },
      { label: "Regenerative tissue biology", value: false, detail: "Symptom management — not regenerative." },
      { label: "Addresses persistent epithelial defects", value: false, detail: "Limited efficacy for severe surface disease." },
      { label: "Surgical / procedural utility", value: false, detail: "Not suited for post-surgical healing or PED." },
      { label: "Predictable clinical pathway", value: false, detail: "Multi-modality, variable adherence." },
    ],
  },
  {
    id: "cryo",
    name: "Cryopreserved Membranes",
    sub: "Frozen amniotic tissue",
    icon: Snowflake,
    rows: [
      { label: "Ambient temperature storage", value: false, detail: "Requires sub-zero / ULT storage." },
      { label: "Preserved extracellular matrix", value: true, detail: "Yes, with cold-chain dependencies." },
      { label: "Single-step application", value: false, detail: "Thawing and rinsing protocols required." },
      { label: "Simplified logistics & reimbursement", value: false, detail: "Cold-chain shipping, in-office freezer." },
      { label: "Reduced waste", value: false, detail: "Limited shelf life increases write-offs." },
    ],
  },
];

export default function Comparison() {
  const [active, setActive] = useState("xcellereyes");
  const current = COMPETITORS.find((c) => c.id === active);
  const Icon = current.icon;

  return (
    <section data-testid="comparison-section" className="relative bg-cloud py-24 md:py-32 overflow-hidden">
      <div className="absolute inset-0 grid-bg opacity-30 pointer-events-none" />
      <div className="relative max-w-7xl mx-auto px-6 lg:px-8">
        <ScrollReveal>
          <div className="text-center max-w-3xl mx-auto mb-16">
            <div className="text-xs tracking-[0.22em] uppercase text-teal font-semibold">
              The XcellerEYES difference
            </div>
            <h2 className="mt-5 font-display text-4xl md:text-5xl text-slate1 leading-tight">
              What makes <span className="text-gradient-teal">XcellerEYES</span> different.
            </h2>
            <p className="mt-6 text-lg text-slate1/65">
              Compare lyophilized regenerative biology against legacy approaches —
              and see why thousands of clinicians are making the switch.
            </p>
          </div>
        </ScrollReveal>

        {/* Tabs */}
        <div className="flex flex-wrap justify-center gap-3 mb-12" data-testid="comparison-tabs">
          {COMPETITORS.map((c) => (
            <button
              key={c.id}
              onClick={() => setActive(c.id)}
              data-testid={`comparison-tab-${c.id}`}
              className={`px-5 py-3 rounded-full text-sm font-semibold transition-all ${
                active === c.id
                  ? "bg-navy text-white shadow-[0_10px_30px_-10px_rgba(0,45,114,0.5)]"
                  : "bg-white text-slate1/70 border border-slate1/10 hover:border-teal hover:text-teal"
              }`}
            >
              {c.name}
            </button>
          ))}
        </div>

        {/* Card */}
        <ScrollReveal>
          <div
            className={`rounded-3xl p-8 md:p-12 transition-all duration-500 ${
              current.highlight
                ? "bg-slate1 text-white shadow-[0_30px_80px_-30px_rgba(0,45,114,0.6)] border border-teal/30"
                : "bg-white text-slate1 shadow-[0_20px_60px_-20px_rgba(0,45,114,0.18)] border border-slate1/5"
            }`}
            data-testid={`comparison-card-${current.id}`}
          >
            <div className="flex items-start justify-between gap-6 flex-wrap mb-10">
              <div className="flex items-center gap-5">
                <div className={`h-16 w-16 rounded-2xl flex items-center justify-center ${current.highlight ? "bg-teal" : "bg-cloud"}`}>
                  <Icon className={`h-7 w-7 ${current.highlight ? "text-white" : "text-navy"}`} />
                </div>
                <div>
                  <div className={`text-xs uppercase tracking-[0.2em] font-semibold ${current.highlight ? "text-teal2" : "text-teal"}`}>
                    {current.highlight ? "RenVia Recommended" : "Comparator"}
                  </div>
                  <div className="font-display text-3xl md:text-4xl font-medium mt-1">{current.name}</div>
                  <div className={`mt-1 text-sm ${current.highlight ? "text-white/65" : "text-slate1/55"}`}>{current.sub}</div>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-12 gap-y-6">
              {current.rows.map((r) => (
                <div key={r.label} className={`flex gap-4 pb-6 border-b ${current.highlight ? "border-white/10" : "border-slate1/10"}`}>
                  <div className={`mt-1 h-7 w-7 flex-shrink-0 rounded-full flex items-center justify-center ${
                    r.value
                      ? current.highlight ? "bg-teal" : "bg-teal"
                      : current.highlight ? "bg-white/10" : "bg-slate1/10"
                  }`}>
                    {r.value ? <Check className="h-4 w-4 text-white" /> : <X className={`h-4 w-4 ${current.highlight ? "text-white/50" : "text-slate1/50"}`} />}
                  </div>
                  <div>
                    <div className={`font-semibold ${current.highlight ? "text-white" : "text-slate1"}`}>{r.label}</div>
                    <div className={`text-sm mt-1 ${current.highlight ? "text-white/60" : "text-slate1/60"}`}>{r.detail}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </ScrollReveal>
      </div>
    </section>
  );
}
